import {
  ASSISTANT_ARTIFACT_HYDRATING_STATE,
  projectAssistantArtifactLongRunningWorkflow,
} from './assistantArtifactLongRunningWorkflow';

export type AssistantTaskListItem = {
  id: string;
  runId: string | null;
  workflowKey: string | null;
  taskClass: string | null;
  origin: {
    sessionId: string | null;
    threadId: string | null;
    userMessageId: string | null;
    assistantMessageId: string | null;
  } | null;
  tenantScopeStatus: 'scoped' | 'legacy_placeholder';
  degradedReason: string | null;
  state: string;
  title: string;
  phaseLabel: string | null;
  statusMessage: string | null;
  progressPercent: number | null;
  resultSummary: string | null;
  route: string | null;
  updatedAt: number | null;
  launchedAt: number | null;
  canCancel: boolean;
  canConfirm: boolean;
  isNew: boolean;
  isOptimistic: boolean;
  artifacts: Array<{
    id: string;
    label: string;
    fileName: string;
    artifactType: string;
    downloadUrl: string;
    expiresAtLabel?: string;
    mimeType?: string | null;
    sourceRef?: string | null;
  }>;
};

export type AssistantOptimisticTaskSeed = {
  id: string;
  runId: string;
  workflowKey: string | null;
  taskClass: string | null;
  title: string;
  route: string | null;
  launchedAt: number;
};

export type AssistantRealtimeRunLike = {
  runId: string;
  state: string;
  module: string;
  route: string;
  workItemId?: string | null;
  origin?: {
    sessionId?: string | null;
    threadId?: string | null;
    userMessageId?: string | null;
    assistantMessageId?: string | null;
  } | null;
  answer: string;
  response?: Record<string, unknown> | null;
  errorMessage?: string | null;
  updatedAtEpochMs: number;
};

export type AssistantRealtimeWorkItemLike = {
  id: string;
  state: string;
  route: string;
  title: string;
  runId?: string | null;
  workflowKey?: string | null;
  taskClass?: string | null;
  statusMessage?: string | null;
  phaseLabel?: string | null;
  progressPercent?: number | null;
  resultSummary?: string | null;
  cancelable?: boolean;
  origin?: {
    sessionId?: string | null;
    threadId?: string | null;
    userMessageId?: string | null;
    assistantMessageId?: string | null;
  } | null;
  tenantScopeStatus?: 'scoped' | 'legacy_placeholder';
  artifactRefs?: ReadonlyArray<{
    id?: string | null;
    label?: string | null;
    fileName?: string | null;
    artifactType?: string | null;
    downloadUrl?: string | null;
    mimeType?: string | null;
    sourceRef?: string | null;
  }> | null;
  updatedAtEpochMs: number;
};

export const TASK_LAUNCH_GRACE_WINDOW_MS = 15_000;

const TERMINAL_TASK_STATES = new Set(['completed', 'failed', 'cancelled', 'expired']);

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const coerceArtifactsFromResponse = (response: Record<string, unknown> | null | undefined): AssistantTaskListItem['artifacts'] => {
  const debug = toRecord(response?.debug);
  const artifactRefs = Array.isArray(debug?.artifactRefs) ? debug.artifactRefs : [];
  return artifactRefs.flatMap((artifact) => {
    const record = toRecord(artifact);
    if (!record || typeof record.downloadUrl !== 'string') {
      return [];
    }
    return [{
      id: String(record.id ?? ''),
      label: String(record.label ?? record.fileName ?? 'Artifact'),
      fileName: String(record.fileName ?? 'artifact'),
      artifactType: String(record.artifactType ?? 'report'),
      downloadUrl: record.downloadUrl,
      expiresAtLabel: undefined,
      mimeType: typeof record.mimeType === 'string' ? record.mimeType : null,
      sourceRef: typeof record.sourceRef === 'string' ? record.sourceRef : null,
    }];
  });
};

const defaultProgress = (state: string): number => (
  projectAssistantArtifactLongRunningWorkflow({ state }).progressPercent ?? 18
);

const isTaskTerminal = (state: string): boolean => {
  const normalized = String(state ?? '').trim().toLowerCase();
  if (normalized === ASSISTANT_ARTIFACT_HYDRATING_STATE) {
    return false;
  }
  return TERMINAL_TASK_STATES.has(normalized);
};

const computeIsNew = (launchedAt: number | null, nowEpochMs: number): boolean => (
  typeof launchedAt === 'number'
  && Number.isFinite(launchedAt)
  && (nowEpochMs - launchedAt) <= TASK_LAUNCH_GRACE_WINDOW_MS
);

const taskMatchKey = (item: { id: string; runId: string | null; workflowKey: string | null }): string =>
  `${item.id}::${item.runId ?? ''}::${item.workflowKey ?? ''}`;

const sameTaskIdentity = (
  left: { id: string; runId: string | null; workflowKey: string | null },
  right: { id: string; runId: string | null; workflowKey: string | null },
): boolean => (
  left.id === right.id
  || (Boolean(left.runId) && left.runId === right.runId)
  || (
    Boolean(left.workflowKey)
    && left.workflowKey === right.workflowKey
    && Boolean(left.runId)
    && Boolean(right.runId)
    && left.runId === right.runId
  )
);

const compareTaskFreshness = (
  left: Pick<AssistantTaskListItem, 'updatedAt' | 'state' | 'progressPercent'>,
  right: Pick<AssistantTaskListItem, 'updatedAt' | 'state' | 'progressPercent'>,
): number => {
  const leftUpdatedAt = Number(left.updatedAt ?? 0);
  const rightUpdatedAt = Number(right.updatedAt ?? 0);
  if (leftUpdatedAt !== rightUpdatedAt) {
    return leftUpdatedAt - rightUpdatedAt;
  }

  const leftTerminal = isTaskTerminal(left.state) ? 1 : 0;
  const rightTerminal = isTaskTerminal(right.state) ? 1 : 0;
  if (leftTerminal !== rightTerminal) {
    return leftTerminal - rightTerminal;
  }

  const leftProgress = Number(left.progressPercent ?? -1);
  const rightProgress = Number(right.progressPercent ?? -1);
  if (leftProgress !== rightProgress) {
    return leftProgress - rightProgress;
  }

  return 0;
};

const coerceRealtimeArtifacts = (
  artifacts: AssistantRealtimeWorkItemLike['artifactRefs'],
): AssistantTaskListItem['artifacts'] => (
  Array.isArray(artifacts)
    ? artifacts.flatMap((artifact) => {
      const downloadUrl = typeof artifact?.downloadUrl === 'string' ? artifact.downloadUrl : null;
      if (!downloadUrl) {
        return [];
      }
      return [{
        id: String(artifact?.id ?? ''),
        label: String(artifact?.label ?? artifact?.fileName ?? 'Artifact'),
        fileName: String(artifact?.fileName ?? 'artifact'),
        artifactType: String(artifact?.artifactType ?? 'report'),
        downloadUrl,
        expiresAtLabel: undefined,
        mimeType: typeof artifact?.mimeType === 'string' ? artifact.mimeType : null,
        sourceRef: typeof artifact?.sourceRef === 'string' ? artifact.sourceRef : null,
      }];
    })
    : []
);

export const buildOptimisticTaskListItem = (
  seed: AssistantOptimisticTaskSeed,
  nowEpochMs: number,
): AssistantTaskListItem => ({
  id: seed.id,
  runId: seed.runId,
  workflowKey: seed.workflowKey,
  taskClass: seed.taskClass,
  origin: null,
  tenantScopeStatus: 'scoped',
  degradedReason: null,
  state: 'queued',
  title: seed.title,
  phaseLabel: 'Queued',
  statusMessage: 'Task started. Waiting for live progress updates.',
  progressPercent: defaultProgress('queued'),
  resultSummary: null,
  route: seed.route,
  updatedAt: nowEpochMs,
  launchedAt: seed.launchedAt,
  canCancel: true,
  canConfirm: false,
  isNew: computeIsNew(seed.launchedAt, nowEpochMs),
  isOptimistic: true,
  artifacts: [],
});

const buildRealtimeTaskListItem = (input: {
  workItem?: AssistantRealtimeWorkItemLike | null;
  run?: AssistantRealtimeRunLike | null;
  launchSeed: AssistantOptimisticTaskSeed | null;
  nowEpochMs: number;
}): AssistantTaskListItem => {
  const workItem = input.workItem ?? null;
  const run = input.run ?? null;
  const workflowProjection = projectAssistantArtifactLongRunningWorkflow({
    taskClass: workItem?.taskClass ?? input.launchSeed?.taskClass ?? null,
    workflowKey: workItem?.workflowKey ?? input.launchSeed?.workflowKey ?? null,
    state: workItem?.state ?? run?.state ?? 'queued',
    phaseLabel: workItem?.phaseLabel ?? null,
    statusMessage: workItem?.statusMessage ?? run?.errorMessage ?? null,
    progressPercent: workItem?.progressPercent ?? null,
    resultSummary: workItem?.resultSummary ?? run?.answer ?? null,
    artifacts: workItem?.artifactRefs ?? [],
    response: run?.response ?? null,
  });
  const artifacts = [
    ...coerceRealtimeArtifacts(workItem?.artifactRefs ?? []),
    ...coerceArtifactsFromResponse(run?.response),
  ].filter((artifact, index, collection) => (
    collection.findIndex((candidate) => (
      candidate.id === artifact.id
      || candidate.downloadUrl === artifact.downloadUrl
      || (
        Boolean(candidate.sourceRef)
        && Boolean(artifact.sourceRef)
        && candidate.sourceRef === artifact.sourceRef
      )
    )) === index
  ));
  const launchedAt = input.launchSeed?.launchedAt
    ?? workItem?.updatedAtEpochMs
    ?? run?.updatedAtEpochMs
    ?? input.nowEpochMs;
  const tenantScopeStatus = workItem?.tenantScopeStatus === 'legacy_placeholder'
    ? 'legacy_placeholder'
    : 'scoped';
  return {
    id: String(workItem?.id ?? run?.workItemId ?? `run:${run?.runId ?? 'unknown'}`),
    runId: workItem?.runId ?? run?.runId ?? null,
    workflowKey: workItem?.workflowKey ?? input.launchSeed?.workflowKey ?? null,
    taskClass: workItem?.taskClass ?? input.launchSeed?.taskClass ?? null,
    origin: workItem?.origin ? {
      sessionId: workItem.origin.sessionId ?? null,
      threadId: workItem.origin.threadId ?? null,
      userMessageId: workItem.origin.userMessageId ?? null,
      assistantMessageId: workItem.origin.assistantMessageId ?? null,
    } : run?.origin ? {
      sessionId: run.origin.sessionId ?? null,
      threadId: run.origin.threadId ?? null,
      userMessageId: run.origin.userMessageId ?? null,
      assistantMessageId: run.origin.assistantMessageId ?? null,
    } : null,
    tenantScopeStatus,
    degradedReason: tenantScopeStatus === 'legacy_placeholder'
      ? 'Historical assistant record used placeholder tenant scope. Read-only only; do not use as deployed-live proof.'
      : null,
    state: workflowProjection.state,
    title: workItem?.title ?? input.launchSeed?.title ?? 'Background task',
    phaseLabel: workflowProjection.phaseLabel,
    statusMessage: workflowProjection.statusMessage,
    progressPercent: workflowProjection.progressPercent,
    resultSummary: workflowProjection.resultSummary,
    route: workItem?.route || run?.route || input.launchSeed?.route || null,
    updatedAt: workItem?.updatedAtEpochMs ?? run?.updatedAtEpochMs ?? input.nowEpochMs,
    launchedAt,
    canCancel: Boolean(workItem ? workItem.cancelable : true) && !isTaskTerminal(workflowProjection.state),
    canConfirm: false,
    isNew: computeIsNew(launchedAt, input.nowEpochMs),
    isOptimistic: false,
    artifacts,
  };
};

const sortTasks = (pathname: string, left: AssistantTaskListItem, right: AssistantTaskListItem): number => {
  const leftNew = left.isNew ? 1 : 0;
  const rightNew = right.isNew ? 1 : 0;
  if (leftNew !== rightNew) {
    return rightNew - leftNew;
  }
  const leftOptimistic = left.isOptimistic ? 1 : 0;
  const rightOptimistic = right.isOptimistic ? 1 : 0;
  if (leftOptimistic !== rightOptimistic) {
    return rightOptimistic - leftOptimistic;
  }
  const leftRouteMatch = left.route === pathname ? 1 : 0;
  const rightRouteMatch = right.route === pathname ? 1 : 0;
  if (leftRouteMatch !== rightRouteMatch) {
    return rightRouteMatch - leftRouteMatch;
  }
  return (right.updatedAt ?? 0) - (left.updatedAt ?? 0);
};

export const mergeAssistantTaskLaneItems = (input: {
  pathname: string;
  activeItems: AssistantTaskListItem[];
  historyItems: AssistantTaskListItem[];
  optimisticSeeds: AssistantOptimisticTaskSeed[];
  realtimeWorkItems: ReadonlyArray<AssistantRealtimeWorkItemLike>;
  realtimeRuns: ReadonlyArray<AssistantRealtimeRunLike>;
  nowEpochMs: number;
}): {
  activeItems: AssistantTaskListItem[];
  recentItems: AssistantTaskListItem[];
} => {
  const launchSeedByRunId = new Map(
    input.optimisticSeeds.map((seed) => [seed.runId, seed] as const)
  );
  const runByRunId = new Map(
    input.realtimeRuns.map((run) => [run.runId, run] as const)
  );
  const runByWorkItemId = new Map<string, AssistantRealtimeRunLike>();
  input.realtimeRuns.forEach((run) => {
    if (run.workItemId) {
      runByWorkItemId.set(run.workItemId, run);
    }
  });

  const mergedItems: Array<AssistantTaskListItem & { __priority: number }> = [];
  const upsertCandidate = (item: AssistantTaskListItem, priority: number) => {
    const existingIndex = mergedItems.findIndex((entry) => sameTaskIdentity(entry, item));
    if (existingIndex < 0) {
      mergedItems.push({ ...item, __priority: priority });
      return;
    }
    const existing = mergedItems[existingIndex]!;
    const freshness = compareTaskFreshness(item, existing);
    const isRunToWorkItemRefinement = priority === 4 && existing.__priority === 3;
    const replace = isRunToWorkItemRefinement
      || freshness > 0
      || (
        freshness === 0
        && priority > existing.__priority
      )
      || (
        priority === existing.__priority
        && freshness === 0
        && sortTasks(input.pathname, item, existing) < 0
      );
    if (replace) {
      mergedItems[existingIndex] = { ...item, __priority: priority };
    }
  };

  input.historyItems.forEach((item) => upsertCandidate(item, 1));
  input.activeItems.forEach((item) => upsertCandidate(item, 2));

  input.realtimeRuns.forEach((run) => {
    const launchSeed = launchSeedByRunId.get(run.runId) ?? null;
    upsertCandidate(buildRealtimeTaskListItem({
      run,
      launchSeed,
      nowEpochMs: input.nowEpochMs,
    }), 3);
  });

  input.realtimeWorkItems.forEach((workItem) => {
    const run = (
      (workItem.runId ? runByRunId.get(workItem.runId) : undefined)
      ?? runByWorkItemId.get(workItem.id)
      ?? null
    );
    const launchSeed = run?.runId
      ? launchSeedByRunId.get(run.runId) ?? null
      : null;
    upsertCandidate(buildRealtimeTaskListItem({
      workItem,
      run,
      launchSeed,
      nowEpochMs: input.nowEpochMs,
    }), 4);
  });

  input.optimisticSeeds.forEach((seed) => {
    const optimisticItem = buildOptimisticTaskListItem(seed, input.nowEpochMs);
    const visible = mergedItems.some((item) => sameTaskIdentity(item, optimisticItem));
    if (!visible) {
      upsertCandidate(optimisticItem, 0);
    }
  });

  const activeItems = mergedItems
    .filter((item) => !isTaskTerminal(item.state))
    .map(({ __priority, ...item }) => item)
    .sort((left, right) => sortTasks(input.pathname, left, right));

  const recentByKey = new Map<string, AssistantTaskListItem>();
  mergedItems
    .filter((item) => isTaskTerminal(item.state))
    .map(({ __priority, ...item }) => item)
    .forEach((item) => {
      const key = taskMatchKey(item);
      const existing = recentByKey.get(key);
      if (!existing || sortTasks(input.pathname, item, existing) < 0) {
        recentByKey.set(key, item);
      }
    });

  return {
    activeItems,
    recentItems: [...recentByKey.values()]
      .sort((left, right) => sortTasks(input.pathname, left, right))
      .slice(0, 12),
  };
};
